import React, { useEffect, useState } from 'react';
import {
    Alert,
    AlertTitle,
    alpha,
    Box,
    Button,
    CircularProgress,
    Divider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import DOMPurify from 'dompurify';
import { TestRunOffer, TestRunResult } from 'api/editor/ai-insight';
import { RecoveredSuggestion } from 'app/extreport/concierge/useInsightPreviewRun';
import IconMi from 'components/common/icon/IconMi';

interface InsightPreviewResultProps {
    t: (key: string, options?: Record<string, unknown>) => string;
    loading: boolean;
    previewingSuggested: boolean;
    result: TestRunResult | null;
    recovered: RecoveredSuggestion | null;
    adopted: boolean;
    /** Caption under an adopted result — surface-specific (dataset mentions Apply). */
    adoptedCaption: string;
    isConditional: boolean;
    fallbackMessage: string;
    idPrefix: string;
    onRunSuggestedPrompt: (offer: TestRunOffer) => void;
    onAdoptSuggestedPrompt: () => void;
    currentName: string;
}

function SuggestedPromptBox({ t, prompt }: { t: InsightPreviewResultProps['t']; prompt: string }) {
    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ fontWeight: 600, fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 2) }}>
                {t('concierge_insight_test_sugg_prompt')}
            </Box>
            <Box
                sx={{
                    mt: 0.5,
                    px: 0.5,
                    borderRadius: '2px',
                    backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                    fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                    whiteSpace: 'pre-wrap',
                    borderLeft: '2px solid',
                    borderLeftColor: (theme) => alpha(theme.palette.text.primary, 0.4),
                }}
            >
                {prompt}
            </Box>
        </Box>
    );
}

export default function InsightPreviewResult({
    t,
    loading,
    previewingSuggested,
    result,
    recovered,
    adopted,
    adoptedCaption,
    isConditional,
    fallbackMessage,
    idPrefix,
    onRunSuggestedPrompt,
    onAdoptSuggestedPrompt,
    currentName,
}: InsightPreviewResultProps) {
    const [tab, setTab] = useState<'significant' | 'not_significant'>('significant');

    // Every fresh run (including a suggested-prompt run) produces a new `result` object,
    // so this also covers resetting the tab when a re-run replaces the shown result.
    useEffect(() => {
        setTab('significant');
    }, [result]);

    if (loading || previewingSuggested) {
        return (
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
                    borderRadius: 1,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    px: 1.5,
                    py: 0.75,
                    color: (theme) => theme.palette.primary.main,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Box>
                        {t(
                            previewingSuggested
                                ? 'concierge_insight_test_previewing_suggested'
                                : 'concierge_insight_test_loading',
                        )}
                    </Box>
                </Stack>
            </Box>
        );
    }

    // Adopt flow: the suggested prompt previewed successfully — success-styled box with the
    // result, the reason the prompt was suggested, and the Update prompt CTA.
    if (recovered && result?.status === 'success') {
        return (
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
                    borderRadius: 1,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    p: 2,
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                    }}
                >
                    <IconMi icon="info-circle" fontSize="16" />
                    <Box>{t('concierge_insight_test_recovered')}</Box>
                </Stack>
                {currentName > '' && (
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize + 2),
                            mt: 1,
                        }}
                    >
                        <IconMi icon="concierge" fontSize="16" />
                        <Box>{currentName}</Box>
                    </Stack>
                )}

                <Box sx={{ pt: 1.5 }}>
                    <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.narrative) }} />
                </Box>
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 2),
                        color: (theme) => alpha(theme.palette.text.primary, 0.64),
                        pt: 1,
                        mt: 1,
                    }}
                >
                    {t('concierge_insight_test_preview_only_suggested')}
                </Box>

                <Box sx={{ pt: 1 }}>
                    <Alert
                        severity="info"
                        variant={'standard'}
                        sx={{
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                            borderRadius: 1,
                            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                        }}
                    >
                        <Box sx={{ fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1) }}>
                            <Box component="span" sx={{ fontWeight: 600 }}>
                                {t('concierge_insight_test_why_suggested')}
                            </Box>{' '}
                            <Box
                                component="span"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(recovered.whyReason) }}
                                sx={{ '& p': { display: 'inline', m: 0 } }}
                            />
                        </Box>
                    </Alert>
                </Box>

                <SuggestedPromptBox t={t} prompt={recovered.suggestedPrompt} />

                <Stack
                    direction={'row'}
                    alignItems="center"
                    spacing={1}
                    sx={{
                        mt: 1,
                        borderTop: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        pt: 1,
                    }}
                >
                    <Button data-test={`${idPrefix}-update-prompt`} variant="outlined" onClick={onAdoptSuggestedPrompt}>
                        {t('concierge_insight_update_prompt')}
                    </Button>
                    <Box
                        sx={{
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 2),
                            color: (theme) => alpha(theme.palette.text.primary, 0.64),
                        }}
                    >
                        {t('concierge_insight_update_prompt_caption')}
                    </Box>
                </Stack>
            </Box>
        );
    }

    if (result?.status === 'success') {
        const showingNotSignificant = isConditional && tab === 'not_significant';
        return (
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
                    borderRadius: 1,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    p: 2,
                }}
            >
                {currentName > '' && (
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize + 2),
                            pb: 2,
                        }}
                    >
                        <IconMi icon="concierge" fontSize="16" />
                        <Box>{currentName}</Box>
                    </Stack>
                )}
                {isConditional && (
                    <Box sx={{ pb: 2 }}>
                        <ToggleButtonGroup value={tab} exclusive onChange={(_, v) => v && setTab(v)}>
                            <ToggleButton data-test={`${idPrefix}-tab-significant`} value="significant">
                                {t('concierge_insight_test_significant')}
                            </ToggleButton>
                            <ToggleButton data-test={`${idPrefix}-tab-not-significant`} value="not_significant">
                                {t('concierge_insight_test_not_significant')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                <Box>
                    {showingNotSignificant ? (
                        <Box sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.64) }}>
                            {fallbackMessage || t('concierge_insight_test_no_output')}
                        </Box>
                    ) : (
                        <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.narrative) }} />
                    )}
                </Box>

                <Box sx={{ pt: 1 }}>
                    <Divider />
                    <Box
                        sx={{
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 2),
                            color: (theme) => alpha(theme.palette.text.primary, 0.64),
                            pt: 1,
                        }}
                    >
                        {adopted ? adoptedCaption : t('concierge_insight_test_preview_caption')}
                    </Box>
                </Box>
            </Box>
        );
    }

    if (result?.status === 'failure') {
        const offer = result.offer;
        return (
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.error.main, 0.24),
                    borderRadius: 1,
                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.04),
                    p: 2,
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                        color: 'error.main',
                        fontWeight: 600,
                        fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize + 2),
                        pb: 1,
                    }}
                >
                    <IconMi icon="alert" fontSize="16" />
                    <Box>{t('concierge_insight_test_fail_title')}</Box>
                </Stack>

                <Box component="div" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.failure_message) }} />

                {offer && (
                    <>
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                borderRadius: 1,
                                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                                mt: 1,
                                p: 1,
                                fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                            }}
                        >
                            <Box>{t('concierge_insight_test_fail_offer')}</Box>
                            <Box sx={{ fontWeight: 600 }}>{offer.description}</Box>
                        </Box>

                        <SuggestedPromptBox t={t} prompt={offer.suggested_prompt} />
                        <Box sx={{ pt: 2 }}>
                            <Button
                                data-test={`${idPrefix}-run-suggested`}
                                variant="outlined"
                                onClick={() => onRunSuggestedPrompt(offer)}
                            >
                                {t('concierge_insight_test_run_suggested')}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        );
    }

    return null;
}
